/**
 * Tracking Injection Utility
 *
 * Handles injection of approved JS payloads into the questionnaire <head>.
 */

import DOMPurify from 'dompurify';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['script', 'noscript', 'meta', 'link', 'img', 'iframe'],
  ALLOWED_ATTR: [
    'src',
    'async',
    'defer',
    'charset',
    'type',
    'content',
    'name',
    'rel',
    'href',
    'id',
    'nonce',
    'width',
    'height',
    'style',
    'frameborder',
    'allow',
    'allowfullscreen',
    'referrerpolicy',
    'loading',
    'alt',
    'title',
    'http-equiv',
  ],
  ALLOW_DATA_ATTR: true,
  KEEP_CONTENT: true,
};

function waitForHeadReady() {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document is not available'));
      return;
    }
    if (document.head) {
      resolve(document.head);
      return;
    }
    if (document.readyState === 'loading') {
      const onReady = () => {
        document.removeEventListener('DOMContentLoaded', onReady);
        if (document.head) {
          resolve(document.head);
        } else {
          reject(new Error('Document head not available after DOMContentLoaded'));
        }
      };
      document.addEventListener('DOMContentLoaded', onReady);
      return;
    }
    reject(new Error('Document head not available'));
  });
}

function removeInjectedTag(containerId) {
  const elements = document.head.querySelectorAll(`[data-tag-container="${containerId}"]`);
  elements.forEach((element) => element.remove());
}

function getSessionStorageItem(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (_) {
    return '';
  }
}

function setSessionStorageItem(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (_) {
    // Tracking cache is best-effort only.
  }
}

/**
 * Inject string payload into <head>.
 * Supports raw JS and HTML snippets with whitelisted tags/attrs.
 */
async function injectHtmlIntoHead(html, containerId) {
  if (!html || !html.trim()) {
    return false;
  }

  await waitForHeadReady();

  const rawHtml = html.trim();
  const looksLikeHtml = /<\s*[a-z!/]/i.test(rawHtml);
  const htmlWithoutNoscript = rawHtml.replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, '');

  removeInjectedTag(containerId);

  if (!looksLikeHtml) {
    const script = document.createElement('script');
    script.setAttribute('data-tag-container', containerId);
    script.textContent = rawHtml;
    document.head.appendChild(script);
    return true;
  }

  const sanitized = DOMPurify.sanitize(htmlWithoutNoscript, SANITIZE_CONFIG);

  // Parse both sanitized and raw payloads. DOMPurify may intentionally strip
  // <script> tags; we preserve vetted script/noscript nodes from raw payload
  // so vendor pixels (Meta, etc.) still execute.
  const sanitizedDiv = document.createElement('div');
  sanitizedDiv.innerHTML = sanitized && sanitized.trim() ? sanitized : '';
  const sanitizedNodes = Array.from(sanitizedDiv.childNodes).filter(
    (node) => node.nodeType === Node.ELEMENT_NODE
  );

  const rawDiv = document.createElement('div');
  rawDiv.innerHTML = htmlWithoutNoscript;
  const rawScriptNodes = Array.from(rawDiv.childNodes).filter((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const tag = node.tagName?.toUpperCase();
    return tag === 'SCRIPT' || tag === 'NOSCRIPT';
  });

  // Build merged list:
  // - keep sanitized non-script nodes
  // - append raw script nodes so executable vendor snippets survive sanitize
  // - skip noscript blocks because dynamically inserting them while JS is enabled
  //   can load fallback tracking pixels and double-count events such as PageView
  const nodes = [
    ...sanitizedNodes.filter((node) => {
      const tag = node.tagName?.toUpperCase();
      return tag !== 'SCRIPT' && tag !== 'NOSCRIPT';
    }),
    ...rawScriptNodes.filter((node) => node.tagName?.toUpperCase() === 'SCRIPT'),
  ];

  if (!nodes.length) {
    throw new Error(`Sanitized HTML is empty for ${containerId}`);
  }

  let injectedCount = 0;

  nodes.forEach((node, index) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node;
    const tagName = element.tagName;
    if (!['SCRIPT', 'NOSCRIPT', 'META', 'LINK', 'IMG', 'IFRAME'].includes(tagName)) {
      return;
    }

    if (tagName === 'SCRIPT') {
      const script = document.createElement('script');
      Array.from(element.attributes).forEach((attr) => {
        if (SANITIZE_CONFIG.ALLOWED_ATTR.includes(attr.name)) {
          script.setAttribute(attr.name, attr.value);
        }
      });
      if (element.textContent) {
        script.textContent = element.textContent;
      }
      script.setAttribute('data-tag-container', containerId);
      script.setAttribute('data-tag-index', index.toString());
      document.head.appendChild(script);
      injectedCount += 1;
      return;
    }

    const cloned = element.cloneNode(true);
    cloned.setAttribute('data-tag-container', containerId);
    cloned.setAttribute('data-tag-index', index.toString());
    document.head.appendChild(cloned);
    injectedCount += 1;
  });

  if (!injectedCount) {
    throw new Error(`Injection failed for ${containerId}: no nodes injected`);
  }

  return true;
}

let conversionTrackingTag = getSessionStorageItem('wellie_conv_tag');
let injectedGlobalJsFingerprint = '';

function getTrackingFingerprint(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export async function injectSocialTags(clientConfig) {
  if (!clientConfig) {
    return;
  }

  const customGlobalJsRaw = clientConfig.custom_global_js || '';
  const conversionTrackingRaw = clientConfig.conversion_tracking_js || '';
  conversionTrackingTag = conversionTrackingRaw.trim();
  setSessionStorageItem('wellie_conv_tag', conversionTrackingTag);

  const customGlobalJs = customGlobalJsRaw.trim();
  const globalFingerprint = getTrackingFingerprint(customGlobalJs);
  if (!customGlobalJs) {
    removeInjectedTag('custom-global-js-container');
    injectedGlobalJsFingerprint = '';
    return;
  }

  if (globalFingerprint && injectedGlobalJsFingerprint === globalFingerprint) {
    return;
  }

  try {
    await injectHtmlIntoHead(customGlobalJs, 'custom-global-js-container');
    injectedGlobalJsFingerprint = globalFingerprint;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Custom Global JS injection failed:', error);
  }
}

function isSafeString(value, maxLength) {
  return typeof value === 'string' && value.length <= maxLength;
}

function isSafeEmail(value) {
  return typeof value === 'string' && /^[^@<>\s]{0,254}@[^\s@<>]{0,254}$/.test(value);
}

function isSafeOrderId(value) {
  return typeof value === 'string' && /^[a-zA-Z0-9-_]{1,64}$/.test(value);
}

function isSafeAmount(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0;
  }
  return typeof value === 'string' && /^\d+(\.\d{1,2})?$/.test(value);
}

function isSafeCoupon(value) {
  return typeof value === 'string' && /^[a-zA-Z0-9-]{0,32}$/.test(value);
}

export async function injectConversionTracking(clientConfig) {
  if (!clientConfig) {
    return;
  }

  const safeConversion = {
    buyer_name: '',
    buyer_email: '',
    order_id: '',
    amount: 0,
    product: '',
    coupon_code: '',
  };

  try {
    const rawData = getSessionStorageItem('welliemd_conversion_data');
    if (rawData) {
      const data = JSON.parse(rawData);

      if (data.buyer_name && isSafeString(data.buyer_name, 255)) safeConversion.buyer_name = data.buyer_name.trim();
      if (data.buyer_email && isSafeEmail(data.buyer_email)) safeConversion.buyer_email = data.buyer_email.trim();
      if (data.order_id && isSafeOrderId(data.order_id)) safeConversion.order_id = data.order_id.trim();
      if (data.amount != null && isSafeAmount(data.amount)) {
        const parsedAmount = typeof data.amount === 'number' ? data.amount : parseFloat(data.amount);
        safeConversion.amount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
      }
      if (data.product && isSafeString(data.product, 1024)) safeConversion.product = data.product.trim();
      if (data.coupon_code && isSafeCoupon(data.coupon_code)) safeConversion.coupon_code = data.coupon_code.trim();
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse conversion data from sessionStorage:', e);
  }

  window.welliemdConversion = safeConversion;
  window.buyer_name = safeConversion.buyer_name || '';
  window.buyer_email = safeConversion.buyer_email || '';
  window.order_id = safeConversion.order_id || '';
  window.amount = safeConversion.amount || 0;
  window.product = safeConversion.product || '';
  window.coupon_code = safeConversion.coupon_code || '';

  const tagToInject = clientConfig.conversion_tracking_js || conversionTrackingTag || '';
  if (!tagToInject) {
    removeInjectedTag('conversion-tracking-container');
    return;
  }

  try {
    await injectHtmlIntoHead(tagToInject, 'conversion-tracking-container');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Conversion Tracking tag injection failed:', error);
  }
}

export function removeConversionTracking() {
  removeInjectedTag('conversion-tracking-container');
}

export function removeAllSocialTags() {
  removeInjectedTag('custom-global-js-container');
  removeInjectedTag('conversion-tracking-container');
  injectedGlobalJsFingerprint = '';
}
